// Envio de uma nova planilha de carteira. Cada envio cria um retrato novo — os
// anteriores continuam consultáveis, por isso importar não pede confirmação;
// apagar um retrato, sim.

import { useState } from 'react';
import { AlertTriangle, Loader2, Trash2, Upload, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { ImportacaoCarteira, mesPorExtenso } from './carteira-types';

interface ResultadoImportacao {
  pis: number;
  pedidos: number;
  faturamentos: number;
  avisos: string[];
}

/** AAAA-MM do mês corrente, que é o default de referência do retrato. */
function mesAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

export function ImportarCarteiraModal({
  importacoes, podeRemover, onFechar, onConcluido,
}: {
  importacoes: ImportacaoCarteira[];
  podeRemover: boolean;
  onFechar: () => void;
  onConcluido: () => void;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [referencia, setReferencia] = useState(mesAtual);
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);
  const [removendo, setRemovendo] = useState<string | null>(null);
  const [confirmarRemocao, setConfirmarRemocao] = useState<string | null>(null);

  const enviar = async () => {
    if (!arquivo) return;
    setEnviando(true);
    setErro(null);
    try {
      const res = await apiService.importarCarteira(arquivo, { referencia, observacao: observacao || undefined });
      if (!res.success) {
        setErro(res.errors?.[0] || res.message || 'Não foi possível importar a planilha');
        return;
      }
      setResultado(res.data as ResultadoImportacao);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha no envio');
    } finally {
      setEnviando(false);
    }
  };

  const remover = async (id: string) => {
    setRemovendo(id);
    setErro(null);
    try {
      const res = await apiService.removerImportacaoCarteira(id);
      if (!res.success) {
        setErro(res.message || 'Não foi possível remover o retrato');
        return;
      }
      onConcluido();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao remover');
    } finally {
      setRemovendo(null);
      setConfirmarRemocao(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">Importar carteira</h2>
          <button
            onClick={onFechar}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
          >
            <X className="h-5 w-5 text-schmersal-gray-medium" aria-hidden />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {resultado ? (
            <div className="space-y-3">
              <p className="text-sm text-schmersal-gray-dark dark:text-white">
                Retrato criado: <strong>{resultado.pis.toLocaleString('pt-BR')} PIs</strong>,{' '}
                <strong>{resultado.faturamentos.toLocaleString('pt-BR')}</strong> lançamentos mensais e{' '}
                <strong>{resultado.pedidos.toLocaleString('pt-BR')}</strong> pedidos em aberto.
              </p>
              {resultado.avisos.length > 0 && (
                <div className="rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-3">
                  <p className="inline-flex items-center gap-2 text-sm font-medium text-schmersal-gray-dark dark:text-white">
                    <AlertTriangle className="h-4 w-4 text-schmersal-yellow-dark" aria-hidden />
                    {resultado.avisos.length} {resultado.avisos.length === 1 ? 'aviso' : 'avisos'} de leitura
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light list-disc pl-5">
                    {resultado.avisos.slice(0, 8).map((a) => <li key={a}>{a}</li>)}
                    {resultado.avisos.length > 8 && <li>… e mais {resultado.avisos.length - 8}</li>}
                  </ul>
                </div>
              )}
              <button
                onClick={onConcluido}
                className="w-full px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
              >
                Ver o painel com esse retrato
              </button>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="i-arquivo" className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Planilha (.xlsx)
                </label>
                <input
                  id="i-arquivo"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setErro(null); }}
                  className="w-full text-sm text-schmersal-gray-dark dark:text-white file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-schmersal-blue file:text-white file:cursor-pointer"
                />
                <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  Precisa ter a aba “PI’s + Saldo”; as abas “Carteira …” alimentam os pedidos em aberto.
                </p>
              </div>

              <div>
                <label htmlFor="i-ref" className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Mês de referência
                </label>
                <input
                  id="i-ref"
                  type="month"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm"
                />
                <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  O mês em que a planilha foi tirada. Ele ainda não fechou, então
                  conta como previsão — faturado é só o que vem antes dele.
                </p>
              </div>

              <div>
                <label htmlFor="i-obs" className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  Observação <span className="font-normal text-schmersal-gray-medium">(opcional)</span>
                </label>
                <input
                  id="i-obs"
                  type="text"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="ex.: fechamento de agosto"
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm"
                />
              </div>

              {erro && (
                <div className="flex items-start gap-2 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-3">
                  <AlertTriangle className="h-4 w-4 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
                  <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">{erro}</p>
                </div>
              )}

              <button
                onClick={enviar}
                disabled={!arquivo || enviando}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Upload className="h-4 w-4" aria-hidden />}
                {enviando ? 'Importando…' : 'Importar'}
              </button>

              {importacoes.length > 0 && (
                <div className="pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
                  <p className="text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide mb-2">
                    Retratos já importados
                  </p>
                  <ul className="space-y-1.5">
                    {importacoes.map((i) => (
                      <li key={i.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0">
                          <span className="text-schmersal-gray-dark dark:text-white">{mesPorExtenso(i.referencia)}</span>
                          <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate">
                            {i.arquivo} · {i.totalPis.toLocaleString('pt-BR')} PIs
                          </span>
                        </span>
                        {podeRemover && (
                          confirmarRemocao === i.id ? (
                            <span className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => remover(i.id)}
                                disabled={removendo === i.id}
                                className="text-xs px-2 py-1 rounded bg-schmersal-yellow-dark text-white disabled:opacity-50"
                              >
                                {removendo === i.id ? 'Removendo…' : 'Confirmar'}
                              </button>
                              <button
                                onClick={() => setConfirmarRemocao(null)}
                                className="text-xs text-schmersal-gray-medium hover:underline"
                              >
                                Cancelar
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setConfirmarRemocao(i.id)}
                              aria-label={`Remover retrato de ${mesPorExtenso(i.referencia)}`}
                              className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark shrink-0"
                            >
                              <Trash2 className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />
                            </button>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
